<template>
    <!-- The default <HeadMeta> in InitWrapper is hydrated lazily, so without LazyHydration here, the default will mount later and override meta -->
    <LazyHydration :hydrate="hydrateOnIdle()">
        <HeadMetaGame
            v-if="game && !game.gameUrlRedirectTarget"
            :breadcrumbs="breadcrumbs"
            :game="game"
            :gameCatInfo="gameCatInfo"
            :prefetchGameURL="buildGameIframeUrl(game)?.href"
        />
    </LazyHydration>
    <Layout :additionalCategories="linkedCategories">
        <div :class="$styleCrit.two_columns_wrapper">
            <main :class="$styleCrit.main">
                <div :class="$styleCrit.game_view">
                    <div :class="$styleCrit.game_screen">
                        <UnavailableGameContainer
                            v-if="game?.gameUrlRedirectTarget"
                            :game="game"
                            :categories="gameCatInfo"
                            :unavailableGameReplace="game.gameUrlRedirectTarget && relatedGames?.[0]"
                        />

                        <GameContainer
                            v-else
                            :game="game"
                            :categories="gameCatInfo"
                            @similarGamesPressed="scrollSimilarGames"
                        />
                    </div>
                    <LazyHydration :hydrate="hydrateOnIdle()">
                        <RightSideCards
                            :games="recommendedGames || []"
                            interfaceTarget="recommended"
                            disableMicrodata
                            :class="$styleCrit.desktop_override"
                        />
                    </LazyHydration>
                </div>
                <div :class="$styleCrit.horizontal_ad">
                    <AdContainer
                        type="leaderboard"
                        :class="$styleCrit.horizontal_ad_1"
                        :backgroundClass="$styleCrit.ad"
                        :emptyClass="$styleCrit.emptyAd"
                        respectHeight
                    />
                    <AdContainer
                        type="leaderboard_rest"
                        :class="$styleCrit.horizontal_ad_2"
                        :backgroundClass="$styleCrit.ad"
                        :emptyClass="$styleCrit.emptyAd"
                        respectHeight
                    />
                </div>
                <LazyHydration
                    v-if="showRelatedGames"
                    :hydrate="hydrateOnIdle()"
                >
                    <div ref="relatedShelfRef">
                        <div :class="$styleCrit.carousel">
                            <GamePageRecommendedShelf
                                type="related"
                                data-interface-section="related"
                                :title="$tr('game.related')"
                                :games="relatedGames || placeholder"
                                disableMicrodata
                            />
                        </div>
                    </div>
                </LazyHydration>
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <GameDetails
                        v-if="game && gameCatInfo && !game.gameUrlRedirectTarget"
                        :game="game"
                        :categoriesInfo="gameCatInfo"
                        :breadcrumbs="breadcrumbs"
                    />
                    <PartnerBlock
                        v-if="game && showPartnerBlock"
                        :gameIcon="game.icon"
                        :hru="game.hru"
                    />
                </LazyHydration>
                <LazyHydration
                    v-if="seriesGameBlock"
                    :hydrate="hydrateOnIdle()"
                >
                    <div :class="$styleCrit.carousel">
                        <GamePageRecommendedShelf
                            type="default"
                            :data-interface-section="seriesGameBlock.groupType"
                            :title="seriesGameBlock.title"
                            :games="seriesGameBlock.games || placeholder"
                            disableMicrodata
                        />
                    </div>
                </LazyHydration>
                <LazyHydration
                    v-if="showRandomGames"
                    :hydrate="hydrateOnIdle()"
                >
                    <div :class="$styleCrit.carousel">
                        <GamePageRecommendedShelf
                            type="random"
                            data-interface-section="random"
                            :title="$tr('game.random')"
                            :games="randomGames || placeholder"
                            disableMicrodata
                        />
                    </div>
                </LazyHydration>
                <LazyHydration :hydrate="hydrateOnIdle()">
                    <PromoCategories />
                    <TheEnd />
                </LazyHydration>
                <ModalReportBug
                    :isOpen="isReportBugModalOpen"
                    :page="route.fullPath"
                    @closeReportModal="layoutStore.setIsReportBugModalOpen(false)"
                />
            </main>
            <RightColumn :class="$styleCrit.right_override" />
        </div>
    </Layout>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, hydrateOnIdle, onMounted, onServerPrefetch, ref, toRef } from 'vue'
import { useRoute } from 'vue-router'

import AdContainer from '@/components_new/AdContainer/AdContainer.vue'
import LazyHydration from '@/components_new/LazyHydration.vue'
import { ModalReportBug } from '@/components_new/Modals/async/ModalReportBug'
import PromoCategories from '@/components_new/PromoCategories/PromoCategories.vue'
import RightColumn from '@/components_new/RightColumn.vue'
import TheEnd from '@/components_new/TheEnd/TheEnd.vue'
import { useTr } from '@/composables/use-tr'
import { DEFAULT_RECOMMENDATION_SIZE } from '@/constants/general'
import Layout from '@/layouts/Layout.vue'
import UnavailableGameContainer from '@/pages/Games/components/GameContainer/UnavailableGame/UnavailableGameContainer.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useSetPageData } from '@/utils/vue-hooks/use-set-page-data'

import GameContainer from './components/GameContainer/GameContainer.vue'
import GameDetails from './components/GameDetails/GameDetails.vue'
import GamePageRecommendedShelf from './components/GamePageShelf/GamePageShelf.vue'
import HeadMetaGame from './components/HeadMetaGame.vue'
import PartnerBlock from './components/PartnerBlock/PartnerBlock.vue'
import RightSideCards from './components/RightSideCards.vue'
import { useGameIframeUrl } from './composables/use-game-iframe-url'
import { useGamePageData, useGameRecomendations } from './use-game-page-data'

const loggerStore = useLoggerStore()
const layoutStore = useLayoutStore()
const appStore = useAppStore()
const { buildGameIframeUrl } = useGameIframeUrl()

const route = useRoute()
const tr = useTr()

const placeholder: undefined[] = Array(DEFAULT_RECOMMENDATION_SIZE).fill(undefined)

const { isReportBugModalOpen } = storeToRefs(layoutStore)

const hru = toRef(() => route.params.game as string)

const { game, gameCatInfo, preloadGameData, processGameResponse } = useGamePageData({
    hru,
    isExport: false,
})

const { recommendations, linkedCategories, preloadRecommendations } = useGameRecomendations({ hru })

const relatedShelfRef = ref<HTMLElement | null>(null)

const seriesGameBlock = computed(() =>
    game.value?.series?.length && recommendations.value?.series?.length
        ? {
              title: gameCatInfo.value?.categoriesInfo?.find((cat) => cat.categoryType === 'series')?.title || '',
              games: recommendations.value.series,
              groupType: 'series',
              rows: 1,
          }
        : null,
)

const breadcrumbs = computed(() =>
    game?.value
        ? [
              {
                  title: tr('game.main_title'),
                  href: { name: ROUTE_NAMES.MAIN },
              },
              ...(gameCatInfo.value?.pathCategories.map((cat) => ({ title: cat.title, href: cat.href })) || []),
              {
                  title: game.value?.title,
                  href: { name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value?.hru } },
                  disabled: true,
              },
          ]
        : [],
)

const relatedGames = computed(() => recommendations.value?.related)
const showRelatedGames = computed(() => (relatedGames.value ? relatedGames.value.length > 0 : true))
const recommendedGames = computed(() => recommendations.value?.recommended)
const randomGames = computed(() => recommendations.value?.random)
const showRandomGames = computed(() => !appStore.isAppEnv && (randomGames.value ? randomGames.value.length > 0 : true))
const showPartnerBlock = computed(
    () => game.value?.is_in_export_catalog && game.value.source === 'playgama' && !appStore.isAppEnv,
)

function scrollSimilarGames() {
    relatedShelfRef?.value?.scrollIntoView({ behavior: 'smooth' })
}

onServerPrefetch(async () => {
    await Promise.all([preloadGameData(), preloadRecommendations()])
    processGameResponse()
})

useSetPageData(() => game.value && { content_category: game.value.main_category, provider: game.value.source || '' })

onMounted(async () => {
    await Promise.all([preloadGameData(), preloadRecommendations()])
    if (processGameResponse()) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'game_preview',
        })
    }
})
</script>
<style module="$styleCrit" critical>
/*
    Style for empty Ad block.
    Cannot not be just "display: none" because Ad container needs to be visible to react on size change.
*/
.emptyAd {
    margin-top: -12px;
}

.two_columns_wrapper {
    display: block;
}

.main {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}

.right_override {
    display: none;
}

.horizontal_ad,
.horizontal_ad_1,
.horizontal_ad_2 {
    display: none;
}

.ad {
    background-color: var(--experimental-purple-graphit);
    border-radius: var(--radius-12);
}

.desktop_override {
    display: none;
}

@media (--desktop-small-plus) {
    .desktop_override {
        display: block;
    }
    .nonDesktop {
        display: none;
    }
    .game_view {
        height: calc(100vw * 9 / 16);
        max-height: 461px;
        display: grid;
        grid-template-columns: 10fr 1fr;
        gap: 12px;
    }
    .game_screen {
        display: flex;
        min-width: 0;
        min-height: 0;
    }
    .horizontal_ad {
        display: flex;
        gap: 8px;
    }
    .horizontal_ad_1 {
        display: block;
        width: 100%;
    }
}

@media (--right-column-fits) {
    .game_view {
        height: calc(100vw * (9 / 16) - var(--global-right-pannel-width));
        min-height: 400px;
        max-height: none;
        grid-template-columns: 8fr 1fr;
    }
    .carousel {
        margin-right: var(--global-wide-scroller-padding-right);
    }

    .two_columns_wrapper {
        display: grid;
        grid-template-columns: 1fr var(--global-right-pannel-width);
        gap: 12px;
    }

    .right_override {
        display: block;
        flex-shrink: 0;
    }
}

@media (--desktop-medium-plus) {
    .game_view {
        grid-template-columns: 7fr 1fr;
    }
}

@media (--desktop-xlarge-plus) {
    .game_view {
        grid-template-columns: 4fr 1fr;
    }
    .game_view {
        max-height: 621px;
    }

    .horizontal_ad_1,
    .horizontal_ad_2 {
        display: block;
        flex-basis: 50%;
        flex-shrink: 0;
        width: 50%;
    }
}
</style>
